import styles from './RiskSlider.module.css';

/* ── Stepped risk-tolerance slider ──
   A real range input underneath so it can be dragged and driven from the
   keyboard; the track, the stops and the knob are drawn over it.

   Shared by the Start copying journey and the manage sheet, which offer the
   same choice and have to draw it the same way. The levels passed in are
   already narrowed to the ones the account may use, so this only renders what
   it is given — it does not know about deposits or the multiplier grid.

   Each label carries both its full name and a three-letter form; CSS picks
   which one shows, since a phone has no room for four full names in a row. */
function RiskSlider({ levels, value, onChange, disabled = false }) {
  const index = Math.max(0, levels.findIndex((l) => l.id === value));
  const last = levels.length - 1;
  const pct = last <= 0 ? 0 : (index / last) * 100;

  return (
    <div className={styles.block}>
      <div className={styles.wrap}>
        <div className={styles.track} />

        {/* The stops run cap to cap — see .stops, which is inset by the track's
            own radius so the outer two sit in the middle of its rounded ends
            rather than tucked inside them. */}
        <div className={styles.stops}>
          {levels.map((level, i) => (
            <span
              key={level.id}
              className={`${styles.stop} ${i <= index ? styles.stopOn : ''}`}
              style={{ left: `${last <= 0 ? 0 : (i / last) * 100}%` }}
            />
          ))}
        </div>

        {/* The knob travels a narrower rail, inset by its own radius: it is
            twice the track's height, so at either extreme this is what keeps it
            flush inside the track instead of hanging off the end. */}
        <div className={styles.rail}>
          {/* Runs from the track's left cap to the centre of the knob. */}
          <div className={styles.trackFill} style={{ width: `calc(${pct}% + var(--rs-radius))` }} />

          <span className={styles.knob} style={{ left: `${pct}%` }} />
        </div>

        <input
          type="range"
          className={styles.range}
          min={0}
          max={Math.max(0, last)}
          step={1}
          value={index}
          disabled={disabled || levels.length < 2}
          onChange={(e) => onChange(levels[Number(e.target.value)].id)}
          aria-label="Risk tolerance"
          aria-valuetext={levels[index]?.label}
        />
      </div>

      <div
        className={styles.labels}
        style={{ gridTemplateColumns: `repeat(${levels.length}, minmax(0, 1fr))` }}
      >
        {levels.map((level, i) => (
          <button
            key={level.id}
            type="button"
            className={`${styles.label} ${level.id === value ? styles.labelOn : ''}`}
            /* Both forms are in the markup and CSS picks one, so the accessible
               name is set explicitly — otherwise it reads as "ConservativeCON"
               and changes with the breakpoint. */
            aria-label={level.label}
            /* The outer labels sit against the ends of the track, so centring
               them would hang half of each one outside the block. */
            style={{ textAlign: i === 0 ? 'left' : i === last ? 'right' : 'center' }}
            disabled={disabled}
            onClick={() => onChange(level.id)}
          >
            <span className={styles.full}>{level.label}</span>
            <span className={styles.short}>{level.short}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default RiskSlider;
